import React, { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { ensureDLToken } from "@/lib/dlToken";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DeliveryHeatmap from "@/components/DeliveryHeatmap";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, LabelList,
  LineChart, Line, ComposedChart, Legend,
} from "recharts";
import { FileDown, FileText, CalendarDays, Route as RouteIcon, Timer, Gauge } from "lucide-react";

function toISODate(d) {
  if (!d) return null;
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${da}`;
}

const RANGES = [
  { v: "this_week", label: "This Week" },
  { v: "last_week", label: "Last Week" },
  { v: "this_month", label: "This Month" },
  { v: "last_month", label: "Last Month" },
];


// Status colors mirror the rest of the app.
const STATUS_COLORS = {
  pending: "#7A9B7A",
  assigned: "#3a86ff",
  in_transit: "#7E57C2",
  delivered: "#234123",
  failed: "#D95D39",
  returned: "#B5651D",
};

const POSITIVE = "#234123"; // deep green
const NEGATIVE = "#D95D39"; // red/orange

export default function AdminReports() {
  const [range, setRange] = useState("this_week");
  const [customRange, setCustomRange] = useState({ from: undefined, to: undefined });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // ---- Pharmacy / Driver filters (above Fleet utilization)
  const [pharmacies, setPharmacies] = useState([]);
  const [driverOptions, setDriverOptions] = useState([]);
  const [pharmacyId, setPharmacyId] = useState("all");
  const [driverId, setDriverId] = useState("all");

  useEffect(() => {
    api.get("/pharmacies").then((r) => setPharmacies(r.data || [])).catch(() => {});
    api.get("/users", { params: { role: "driver" } })
      .then((r) => setDriverOptions(r.data || []))
      .catch(() => {});
  }, []);

  const load = (r = range, custom = customRange, ph = pharmacyId, drv = driverId) => {
    const params = { range: r };
    if (r === "custom") {
      const s = toISODate(custom.from);
      const e = toISODate(custom.to || custom.from);
      if (!s || !e) return;
      params.start = s;
      params.end = e;
    }
    if (ph && ph !== "all") params.pharmacy_id = ph;
    if (drv && drv !== "all") params.driver_id = drv;
    setLoading(true);
    return api.get("/stats/reports", { params })
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(range, customRange, pharmacyId, driverId); /* eslint-disable-next-line */ }, [range, customRange, pharmacyId, driverId]);

  useEffect(() => {
    const id = setInterval(() => load(range, customRange, pharmacyId, driverId), 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [range, customRange, pharmacyId, driverId]);

  const exportFile = async (kind) => {
    // SEC-003: short-lived download token instead of the 7-day JWT.
    const token = await ensureDLToken();
    let qs = `range=${encodeURIComponent(range)}`;
    if (range === "custom") {
      const s = toISODate(customRange.from);
      const e = toISODate(customRange.to || customRange.from);
      qs += `&start=${encodeURIComponent(s)}&end=${encodeURIComponent(e)}`;
    }
    if (pharmacyId && pharmacyId !== "all") qs += `&pharmacy_id=${encodeURIComponent(pharmacyId)}`;
    if (driverId && driverId !== "all") qs += `&driver_id=${encodeURIComponent(driverId)}`;
    qs += `&auth=${encodeURIComponent(token || "")}`;
    const url = `${API}/stats/reports/export.${kind}?${qs}`;
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // --- Derived chart data
  const customers = useMemo(() => (data?.orders_by_customer || []), [data]);
  const statuses = useMemo(() => (
    Object.entries(data?.status_counts || {}).map(([k, v]) => ({
      name: k.replace("_", " "),
      key: k,
      value: v,
    }))
  ), [data]);
  const drivers = useMemo(() => (data?.driver_effectiveness || []), [data]);
  const driversAssigned = useMemo(() => (data?.driver_assigned_orders || []), [data]);
  const driverMiles = useMemo(() => (data?.driver_miles || []), [data]);
  const routes = useMemo(() => (data?.routes_profit || []), [data]);
  const driverFixedPay = data?.driver_fixed_pay ?? 120;

  if (!data) {
    return <div className="p-10 text-muted-foreground" data-testid="reports-loading">Loading reports…</div>;
  }

  const customPillLabel = (range === "custom" && customRange.from)
    ? `${toISODate(customRange.from)}${customRange.to ? ` → ${toISODate(customRange.to)}` : ""}`
    : "Custom…";

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] mx-auto" data-testid="admin-reports-page">
      <header className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl lg:text-4xl font-display font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground mt-1.5" data-testid="reports-range-label">
            <span className="font-medium text-foreground">{data.range_label}</span>
            <span className="mx-1.5 text-muted-foreground/60">·</span>
            <span className="font-mono-num">{data.range_start} → {data.range_end}</span>
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div
            className="inline-flex rounded-full bg-secondary/60 p-1 flex-wrap"
            role="radiogroup"
            aria-label="Report range"
            data-testid="reports-range-toggle"
          >
            {RANGES.map((opt) => {
              const active = range === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setRange(opt.v)}
                  className={`px-3 h-8 text-[11px] font-medium uppercase tracking-wider rounded-full transition-colors ${
                    active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                  } ${loading && active ? "animate-pulse" : ""}`}
                  data-testid={`reports-range-${opt.v}`}
                >
                  {opt.label}
                </button>
              );
            })}
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  onClick={() => setRange("custom")}
                  className={`inline-flex items-center gap-1.5 px-3 h-8 text-[11px] font-medium uppercase tracking-wider rounded-full transition-colors ${
                    range === "custom"
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="reports-range-custom"
                  aria-pressed={range === "custom"}
                >
                  <CalendarDays className="h-3.5 w-3.5" />
                  {customPillLabel}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto p-0" data-testid="reports-range-custom-popover">
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  defaultMonth={customRange.from || new Date()}
                  selected={customRange}
                  onSelect={(val) => {
                    setCustomRange(val || { from: undefined, to: undefined });
                    if (val?.from && val?.to) setPickerOpen(false);
                  }}
                  initialFocus
                />
                <div className="flex justify-between items-center px-3 py-2 border-t border-border text-xs">
                  <span className="text-muted-foreground">Pick a start, then an end day.</span>
                  <button
                    type="button"
                    onClick={() => { setCustomRange({ from: undefined, to: undefined }); setRange("this_week"); setPickerOpen(false); }}
                    className="font-medium text-primary hover:underline"
                    data-testid="reports-range-custom-reset"
                  >
                    Reset to This Week
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <Button variant="outline" className="rounded-full" onClick={() => exportFile("csv")} data-testid="export-csv-btn">
            <FileDown className="h-4 w-4 mr-1.5" /> CSV
          </Button>
          <Button className="rounded-full" onClick={() => exportFile("pdf")} data-testid="export-pdf-btn">
            <FileText className="h-4 w-4 mr-1.5" /> PDF
          </Button>
        </div>
      </header>

      {/* Pharmacy + Driver filters — applies to every report block below */}
      <Card className="p-4 mb-4 rounded-xl border-border bg-card/60" data-testid="reports-filters">
        <div className="grid sm:grid-cols-2 gap-4 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold block mb-1.5">
              Pharmacy
            </label>
            <Select value={pharmacyId} onValueChange={setPharmacyId} data-testid="reports-filter-pharmacy">
              <SelectTrigger data-testid="reports-filter-pharmacy-trigger">
                <SelectValue placeholder="All pharmacies" />
              </SelectTrigger>
              <SelectContent className="z-[9999]">
                <SelectItem value="all" data-testid="reports-filter-pharmacy-all">All pharmacies</SelectItem>
                {pharmacies.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`reports-filter-pharmacy-${p.id}`}>
                    {p.nickname || p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold block mb-1.5">
              Driver
            </label>
            <Select value={driverId} onValueChange={setDriverId} data-testid="reports-filter-driver">
              <SelectTrigger data-testid="reports-filter-driver-trigger">
                <SelectValue placeholder="All drivers" />
              </SelectTrigger>
              <SelectContent className="z-[9999]">
                <SelectItem value="all" data-testid="reports-filter-driver-all">All drivers</SelectItem>
                {driverOptions.map((d) => (
                  <SelectItem key={d.id} value={d.id} data-testid={`reports-filter-driver-${d.id}`}>
                    {d.name || d.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {(pharmacyId !== "all" || driverId !== "all") && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
            <span className="text-[11px] text-muted-foreground">
              Filters active — every block below is scoped to the selection.
            </span>
            <button
              type="button"
              onClick={() => { setPharmacyId("all"); setDriverId("all"); }}
              className="text-[11px] font-medium text-primary hover:underline"
              data-testid="reports-filter-clear"
            >
              Clear filters
            </button>
          </div>
        )}
      </Card>

      {/* Delivery density heatmap — respects the same filter scope. */}
      <DeliveryHeatmap
        range={range}
        customRange={customRange}
        pharmacyId={pharmacyId}
        driverId={driverId}
      />


      {/* Fleet utilization — headline metrics across the selected range. */}
      <FleetMetricsRow data={data} />

      {/* 2x2 grid of 4 bar charts */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* 1. Quantity Orders by Customer (pharmacy) */}
        <Card className="p-5 rounded-xl border-border bg-card/80" data-testid="chart-orders-by-customer">
          <h2 className="font-display font-semibold text-base mb-3">
            Orders by Customer · <span className="text-muted-foreground font-normal">{data.range_label}</span>
          </h2>
          <div style={{ width: "100%", height: 320 }}>
            {customers.length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">
                No orders in this period.
              </div>
            ) : (
              <ResponsiveContainer>
                <BarChart
                  data={customers}
                  layout="vertical"
                  margin={{ top: 8, right: 30, left: 8, bottom: 8 }}
                >
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={140}
                    tick={{ fontSize: 11 }}
                    stroke="hsl(var(--muted-foreground))"
                  />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }} />
                  <Bar dataKey="orders" name="Orders" fill="hsl(120 30% 25%)" radius={[0, 6, 6, 0]}>
                    <LabelList dataKey="orders" position="right" style={{ fontSize: 11, fill: "hsl(var(--foreground))" }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* 2. Quantity Orders by Status */}
        <Card className="p-5 rounded-xl border-border bg-card/80" data-testid="chart-orders-by-status">
          <h2 className="font-display font-semibold text-base mb-3">
            Orders by Status · <span className="text-muted-foreground font-normal">{data.range_label}</span>
          </h2>
          <div style={{ width: "100%", height: 320 }}>
            {statuses.every((s) => s.value === 0) ? (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">
                No orders in this period.
              </div>
            ) : (
              <ResponsiveContainer>
                <BarChart data={statuses} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }} />
                  <Bar dataKey="value" name="Orders" radius={[6, 6, 0, 0]}>
                    {statuses.map((s) => (
                      <Cell key={s.key} fill={STATUS_COLORS[s.key] || "hsl(var(--primary))"} />
                    ))}
                    <LabelList dataKey="value" position="top" style={{ fontSize: 11, fill: "hsl(var(--foreground))" }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* 3. Orders Assigned per Driver — workload snapshot */}
        <Card className="p-5 rounded-xl border-border bg-card/80" data-testid="chart-orders-assigned-per-driver">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-semibold text-base">
              Orders Assigned per Driver · <span className="text-muted-foreground font-normal">{data.range_label}</span>
            </h2>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Total orders</span>
          </div>
          <div style={{ width: "100%", height: 320 }}>
            {driversAssigned.length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">
                No orders assigned to drivers in this period.
              </div>
            ) : (
              <ResponsiveContainer>
                <BarChart
                  data={driversAssigned}
                  layout="vertical"
                  margin={{ top: 8, right: 30, left: 8, bottom: 8 }}
                >
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }} />
                  <Bar dataKey="orders" name="Orders" fill="#3a86ff" radius={[0, 6, 6, 0]}>
                    <LabelList dataKey="orders" position="right" style={{ fontSize: 11, fill: "hsl(var(--foreground))" }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* 3b. Miles per Driver — total route mileage attributed to each driver
             in the selected range. Purple bars per operator request (2026-07-25). */}
        <Card className="p-5 rounded-xl border-border bg-card/80" data-testid="chart-miles-per-driver">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-semibold text-base">
              Miles per Driver · <span className="text-muted-foreground font-normal">{data.range_label}</span>
            </h2>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Route distance (mi)</span>
          </div>
          <div style={{ width: "100%", height: 320 }}>
            {driverMiles.length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">
                No driver mileage in this period.
              </div>
            ) : (
              <ResponsiveContainer>
                <BarChart
                  data={driverMiles}
                  layout="vertical"
                  margin={{ top: 8, right: 40, left: 8, bottom: 8 }}
                >
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11 }}
                    stroke="hsl(var(--muted-foreground))"
                    tickFormatter={(v) => `${v} mi`}
                  />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }}
                    formatter={(value) => [`${Number(value).toFixed(1)} mi`, "Distance"]}
                  />
                  {/* Purple — matches the "Pickup / Rx" accent used elsewhere in the app */}
                  <Bar dataKey="miles" name="Miles" fill="#7c3aed" radius={[0, 6, 6, 0]}>
                    <LabelList
                      dataKey="miles"
                      position="right"
                      formatter={(v) => `${Number(v).toFixed(1)} mi`}
                      style={{ fontSize: 11, fill: "hsl(var(--foreground))" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* 4. Driver Effectiveness — % delivered vs % failed */}
        <Card className="p-5 rounded-xl border-border bg-card/80" data-testid="chart-driver-effectiveness">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-semibold text-base">
              Drivers Effectivity · <span className="text-muted-foreground font-normal">{data.range_label}</span>
            </h2>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">% Delivered vs % Failed</span>
          </div>
          <div style={{ width: "100%", height: 320 }}>
            {drivers.length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">
                No driver activity in this period.
              </div>
            ) : (
              <ResponsiveContainer>
                <BarChart
                  data={drivers}
                  layout="vertical"
                  stackOffset="expand"
                  margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                >
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    domain={[0, 1]}
                    tickFormatter={(v) => `${Math.round(v * 100)}%`}
                    tick={{ fontSize: 11 }}
                    stroke="hsl(var(--muted-foreground))"
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={140}
                    tick={{ fontSize: 11 }}
                    stroke="hsl(var(--muted-foreground))"
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }}
                    formatter={(value, name, item) => {
                      // BUG FIX (2026-07-29 prod): Recharts passes the Bar's
                      // `name` prop (capitalized "Delivered"/"Failed") as the
                      // 2nd arg, not the lowercase `dataKey`. Previous check
                      // `key === "delivered"` was always false → both rows
                      // labelled "Failed". Compare against dataKey via `item`
                      // for correctness even if `name` labels change.
                      const total = item?.payload?.total || 0;
                      const pct = total ? Math.round((value / total) * 100) : 0;
                      const dk = item?.dataKey || (typeof name === "string" ? name.toLowerCase() : "");
                      const label = dk === "delivered" ? "Delivered" : "Failed";
                      return [`${value} (${pct}%)`, label];
                    }}
                  />
                  <Bar dataKey="delivered" stackId="eff" fill={POSITIVE} name="Delivered">
                    <LabelList
                      dataKey="pct_delivered"
                      position="center"
                      formatter={(v) => (v >= 12 ? `${v}%` : "")}
                      style={{ fontSize: 11, fill: "#fff" }}
                    />
                  </Bar>
                  <Bar dataKey="failed" stackId="eff" fill={NEGATIVE} name="Failed" radius={[0, 6, 6, 0]}>
                    <LabelList
                      dataKey="pct_failed"
                      position="center"
                      formatter={(v) => (v >= 12 ? `${v}%` : "")}
                      style={{ fontSize: 11, fill: "#fff" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* 4. Routes Profit — revenue per route minus $120 driver fixed pay */}
        <Card className="p-5 rounded-xl border-border bg-card/80" data-testid="chart-routes-profit">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-semibold text-base">
              Routes Profit · <span className="text-muted-foreground font-normal">{data.range_label}</span>
            </h2>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              vs ${driverFixedPay.toFixed(0)} driver pay
            </span>
          </div>
          <div style={{ width: "100%", height: 320 }}>
            {routes.length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">
                No routes in this period.
              </div>
            ) : (
              <ResponsiveContainer>
                <BarChart data={routes} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis
                    reversed
                    tickFormatter={(v) => `$${v}`}
                    tick={{ fontSize: 11 }}
                    stroke="hsl(var(--muted-foreground))"
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }}
                    formatter={(value, _key, item) => {
                      const p = item?.payload || {};
                      return [
                        `$${Number(value).toFixed(2)}`,
                        `Profit (rev $${p.revenue?.toFixed?.(2) || "0.00"} − cost $${p.cost?.toFixed?.(2) || "0.00"})`,
                      ];
                    }}
                  />
                  <Bar dataKey="profit" name="Profit" radius={[6, 6, 0, 0]}>
                    {routes.map((r, i) => (
                      <Cell key={i} fill={r.profit >= 0 ? POSITIVE : NEGATIVE} />
                    ))}
                    <LabelList
                      dataKey="profit"
                      position="bottom"
                      formatter={(v) => `$${Number(v).toFixed(0)}`}
                      style={{ fontSize: 11, fill: "hsl(var(--foreground))" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function fmtDriveTime(minutes) {
  if (minutes == null) return "—";
  const m = Math.round(Number(minutes) || 0);
  if (m <= 0) return "0 min";
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r} min`;
  if (r === 0) return `${h} h`;
  return `${h} h ${r} min`;
}

function FleetMetricsRow({ data }) {
  const miles = data.fleet_total_miles ?? 0;
  const minutes = data.fleet_total_minutes ?? 0;
  const drives = data.fleet_total_drives ?? 0;
  const stops = data.fleet_delivery_stops ?? 0;
  const perDelivery = data.fleet_miles_per_delivery;
  const headline = drives === 0
    ? "No routes driven in this period yet."
    : `${miles.toLocaleString()} mi · ${fmtDriveTime(minutes)} driven · ${data.range_label.toLowerCase()} · ${stops} deliver${stops === 1 ? "y" : "ies"}${perDelivery != null ? ` → ${perDelivery.toLocaleString()} mi per delivery` : ""}.`;

  return (
    <section className="mb-6" data-testid="fleet-metrics-row">
      <h2 className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">Fleet utilization</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <FleetTile icon={RouteIcon} tone="primary" label="Total miles" value={miles.toLocaleString()} unit="mi" hint={`${drives} route${drives === 1 ? "" : "s"}`} testid="fleet-tile-miles" />
        <FleetTile icon={Timer} tone="sky" label="Drive time" value={fmtDriveTime(minutes)} unit="" hint={`${(minutes / 60).toFixed(1)} h decimal`} testid="fleet-tile-time" />
        <FleetTile icon={Gauge} tone="amber" label="Per delivery" value={perDelivery != null ? perDelivery.toString() : "—"} unit={perDelivery != null ? "mi" : ""} hint={`${stops} delivery stop${stops === 1 ? "" : "s"}`} testid="fleet-tile-per-delivery" />
        <FleetTile icon={RouteIcon} tone="green" label="Deliveries" value={stops.toLocaleString()} unit="" hint="on driven routes" testid="fleet-tile-deliveries" />
      </div>
      <p className="text-xs text-muted-foreground mt-2 italic" data-testid="fleet-headline">{headline}</p>
    </section>
  );
}

const FLEET_TONE_BG = {
  primary: "bg-primary/10 text-primary",
  sky: "bg-[#3a86ff]/12 text-[#1f4ea3]",
  amber: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200",
  green: "bg-accent/15 text-accent",
};

function FleetTile({ icon: Icon, tone, label, value, unit, hint, testid }) {
  return (
    <Card className="p-3 rounded-xl border-border bg-card/80" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className={`h-9 w-9 rounded-lg grid place-items-center ${FLEET_TONE_BG[tone] || FLEET_TONE_BG.primary}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
          <div className="flex items-baseline gap-1 mt-0.5">
            <span className="font-display font-semibold text-xl leading-none">{value}</span>
            {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
          </div>
          {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
        </div>
      </div>
    </Card>
  );
}
